// Book Preview tab: the book's look-inside file (a PDF of sample pages) —
// uploaded, replaced or removed here; once there, Preview opens it in the book
// player the store page uses ("Look inside"), full screen.
import React, { useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { ArrowPathIcon, BookOpenIcon, CloudArrowUpIcon, DocumentTextIcon, TrashIcon } from "@heroicons/react/24/outline"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { UploadPreview, DeletePreview } from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"
import { Alert, IconButton } from "../shared/shared"
import { LookInsideViewer } from "../../../Storefront/ui/LookInsideButton"

// the server's limit (aldoo-com-api product-preview-upload)
const MAX_MB = 50

export default function BookPreviewTab({ editor }) {
  const t = useProductEditorTexts()
  const { pid, product, reload } = editor
  const [error, setError] = useState("")
  const [uploading, setUploading] = useState(false)
  const [open, setOpen] = useState(false)
  const fileInput = useRef(null)
  const url = product?.previewUrl || ""

  const upload = async (file) => {
    if (!file) return
    setError("")
    if (!/\.pdf$/i.test(file.name)) return setError(t`The preview must be a PDF file`)
    if (file.size > MAX_MB * 1024 * 1024) return setError(t("The preview may be at most %s MB", MAX_MB))
    setUploading(true)
    try {
      const res = await UploadPreview(pid, file)
      if (errorMessage(res)) return setError(`${t`Upload failed`}: ${errorMessage(res)}`)
      await reload()
    } catch (e) {
      setError(`${t`Upload failed`}: ${e?.response?.data?.error || e?.message || ""}`)
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ""
    }
  }

  const remove = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Remove the book preview`,
      message: t`The store page no longer offers "Look inside" for this book.`,
      confirmText: t`Remove`,
      cancelText: t`Cancel`,
      onConfirm: async () => {
        setError("")
        const res = await DeletePreview(pid).catch((e) => ({ error: e?.message }))
        if (errorMessage(res)) return setError(errorMessage(res))
        await reload()
      },
    })

  return (
    <div className="flex flex-col gap-3" data-testid="book-preview-tab">
      <Typography component="h4" variant="admin_h4" className="my-0">{t`Book Preview`}</Typography>
      <p className="text-sm text-admin_text_secondary mt-0 mb-1">
        {t`A PDF with sample pages of the book. Buyers open it with "Look inside" on the product page, where it turns like a book.`}
      </p>
      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

      <input ref={fileInput} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="book-preview-file" />

      {url ? (
        <div className="flex flex-wrap items-center gap-3 p-3 border border-admin_bordered rounded-md" data-testid="book-preview-file-row">
          <DocumentTextIcon className="w-7 h-7 text-admin_primary shrink-0" />
          <div className="flex-1 min-w-[160px]">
            <div className="text-sm font-medium">{t`Sample pages (PDF)`}</div>
            <div className="text-xs text-admin_text_secondary">{t`Shown with "Look inside" on the product page`}</div>
          </div>
          <Button variant="admin_primary" className="h-9 px-4" onClick={() => setOpen(true)} data-testid="book-preview-open">
            <BookOpenIcon className="w-4 h-4" /> {t`Preview`}
          </Button>
          <IconButton title={t`Replace the file`} onClick={() => fileInput.current?.click()} disabled={uploading}>
            <ArrowPathIcon className="w-4 h-4" />
          </IconButton>
          <IconButton title={t`Remove the book preview`} className="hover:text-admin_error" onClick={remove}>
            <TrashIcon className="w-4 h-4" />
          </IconButton>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-admin_bordered rounded-md text-admin_text_secondary hover:border-admin_primary hover:text-admin_primary transition-colors"
          data-testid="book-preview-upload"
        >
          <CloudArrowUpIcon className="w-8 h-8" />
          <span className="text-sm font-semibold">{t`Upload a PDF`}</span>
          <span className="text-xs">{t("PDF, up to %s MB", MAX_MB)}</span>
        </button>
      )}
      {uploading && (
        <div className="h-1 bg-admin_primary/30 overflow-hidden rounded">
          <div className="h-full w-1/3 bg-admin_primary animate-pulse" />
        </div>
      )}

      {open && url && <LookInsideViewer url={url} title={t`Book Preview`} onClose={() => setOpen(false)} />}
    </div>
  )
}
